import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { get } from 'lodash';
import { ChevronDown, ChevronUp, ArrowUp } from 'lucide-react';

import { OrderSchemaType } from '../order.schema';
import { rateioSellingPrice, RateioItemInput } from '../utils/rateioSellingPrice';
import { orderItemCardId, EXPAND_ITEM_EVENT } from '../components/OrderStickyStepper';
import CurrencyInput from '../../../../../components/CurrencyInput';
import ConfirmationModal from '../../../../../components/ModalConfirmation';

import {
  Title,
  Button,
  FormGrid,
  FormGroup,
  Label,
  Input,
} from '../style';
import { Summary, SummaryItem, SummaryLabel, SummaryValue } from './Items/ItemsSection.style';
import {
  FechamentoContainer,
  FechamentoHeader,
  HeaderPriceBlock,
  HeaderPriceRow,
  StrongLabel,
  TableScroll,
  RowGrid,
  ColHead,
  ItemRowCard,
  ItemRow,
  ItemName,
  Num,
  ExpandButton,
  Breakdown,
  BreakdownRow,
  BreakdownIndex,
  TypeChip,
  BreakdownDesc,
  BreakdownFormula,
  BreakdownValue,
  AdjustLink,
  EmptyState,
  RowsList,
} from './ReviewSection.style';


const TYPE_LABELS: Record<string, string> = {
  MATERIAL: 'Matéria Prima',
  PRODUCT: 'Produto Final',
  RESOURCE: 'Insumos',
  SERVICE: 'Serviço',
};

const TYPE_CHIP: Record<string, string> = {
  MATERIAL: 'MP',
  PRODUCT: 'PE',
  RESOURCE: 'Insumo',
  SERVICE: 'Serviço',
};

const formatCurrency = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Uma linha de composição do breakdown (leitura). */
interface BreakdownLine {
  chip: string;
  desc: string;
  formula?: string;
  value: number | null;
}

/** Monta as linhas de composição de um item a partir do form state (sem lógica de escrita). */
const buildBreakdown = (it: any): BreakdownLine[] => {
  const lines: BreakdownLine[] = [];
  const unitName = it?.unitOfMeasurementName || '';

  const materialLine = (source: any, qty: number | null, unitPrice: number | null): BreakdownLine => {
    const desc = [source?.materiaPrima, source?.stockDescription].filter(Boolean).join(' — ');
    const value = qty != null && unitPrice != null && qty > 0 && unitPrice > 0 ? qty * unitPrice : null;
    return {
      chip: TYPE_CHIP.MATERIAL,
      desc: desc || 'Matéria-prima',
      formula: qty != null && unitPrice != null && qty > 0 && unitPrice > 0
        ? `${qty.toLocaleString('pt-BR')}${unitName ? ` ${unitName}` : ''} × ${formatCurrency(unitPrice)}`
        : undefined,
      value,
    };
  };

  const serviceLine = (sub: any): BreakdownLine => {
    const desc = sub?.stage?.servico || sub?.nomeCustomizado || 'Serviço';
    const cost = sub?.cost != null ? Number(sub.cost) : null;
    const markup = sub?.markup != null ? Number(sub.markup) : null;
    const qty = sub?.stage?.quantidade != null ? Number(sub.stage.quantidade) : (sub?.quantity != null ? Number(sub.quantity) : null);
    const value = sub?.itemPrice != null ? Number(sub.itemPrice)
      : (sub?.priceDetails?.itemSubtotal != null ? Number(sub.priceDetails.itemSubtotal) : null);
    const formula = cost != null && cost > 0
      ? `custo ${formatCurrency(cost)}${qty != null && qty > 0 ? ` × ${qty.toLocaleString('pt-BR')}` : ''}${markup != null && markup > 0 ? ` × ${markup.toLocaleString('pt-BR')}` : ''}`
      : undefined;
    return { chip: TYPE_CHIP.SERVICE, desc, formula, value };
  };

  const itemType = String(it?.itemType || '');
  const qty = it?.quantity != null ? Number(it.quantity) : null;
  const unitPrice = it?.priceDetails?.unitPrice != null ? Number(it.priceDetails.unitPrice) : null;

  if (itemType === 'MATERIAL' && it?.feedstock) {
    lines.push(materialLine(it.feedstock, qty, unitPrice));
  } else if (itemType === 'PRODUCT' && it?.finalProduct?.produto) {
    lines.push({ chip: TYPE_CHIP.PRODUCT, desc: it.finalProduct.produto, value: null });
  } else if (itemType === 'RESOURCE' && it?.resource?.insumo) {
    const value = qty != null && unitPrice != null && qty > 0 && unitPrice > 0 ? qty * unitPrice : null;
    lines.push({ chip: TYPE_CHIP.RESOURCE, desc: it.resource.insumo, value });
  } else if (itemType === 'SERVICE') {
    lines.push(serviceLine(it));
  }

  (Array.isArray(it?.subItems) ? it.subItems : []).forEach((sub: any) => {
    const subType = String(sub?.itemType || '');
    if (subType === 'SERVICE') {
      lines.push(serviceLine(sub));
    } else if (subType === 'MATERIAL') {
      const subQty = sub?.feedstock?.quantidade != null ? Number(sub.feedstock.quantidade) : null;
      const subUnitPrice = sub?.priceDetails?.unitPrice != null ? Number(sub.priceDetails.unitPrice) : null;
      lines.push(materialLine(sub?.feedstock, subQty, subUnitPrice));
    } else if (sub?.nomeCustomizado || sub?.resource?.insumo) {
      lines.push({
        chip: TYPE_CHIP[subType] || subType,
        desc: sub?.resource?.insumo || sub?.nomeCustomizado,
        value: sub?.itemPrice != null ? Number(sub.itemPrice) : null,
      });
    }
  });

  return lines;
};

const ReviewSection: React.FC = () => {
  const [showRateioConfirm, setShowRateioConfirm] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { control, register, setValue, watch, getValues, formState: { errors } } = useFormContext<OrderSchemaType>();

  const sellingPriceOrder = watch('sellingPrice');
  const items = watch('items') || [];

  const totalPadrao = items.reduce(
    (acc, it) => acc + (it.isSaved && it.itemPrice != null ? Number(it.itemPrice) : 0),
    0,
  );

  const itensElegiveisRateio = items.filter(
    (it) => it.isSaved && it.itemPrice != null && Number(it.itemPrice) > 0,
  );

  const itensSalvos = items.filter((it) => it.isSaved);
  const totalPecas = itensSalvos.reduce((acc, it: any) => acc + (Number(it.quantityPieces) || 0), 0);

  const somaEfetivoItens = items.reduce((acc, it) => {
    if (!it.isSaved) return acc;
    const efetivo = it.priceDetails?.sellingPrice ?? it.itemPrice;
    return acc + (efetivo != null ? Number(efetivo) : 0);
  }, 0);

  const round2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;
  const somaAtual = round2(somaEfetivoItens);
  const prevSomaRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevSomaRef.current;
    prevSomaRef.current = somaAtual;
    if (somaAtual <= 0) return;
    const atual = round2(Number(getValues('sellingPrice')) || 0);
    const estavaSincronizado = atual === 0 || (prev != null && Math.abs(atual - prev) < 0.005);
    if (estavaSincronizado && Math.abs(atual - somaAtual) >= 0.005) {
      setValue('sellingPrice', somaAtual, { shouldDirty: true, shouldValidate: false });
    }
  }, [somaAtual, getValues, setValue]);

  const mostrarDistribuir = !!sellingPriceOrder && Number(sellingPriceOrder) > 0
    && Math.abs(Number(sellingPriceOrder) - somaAtual) > 0.01
    && itensElegiveisRateio.length > 0;

  const aplicarRateio = useCallback(() => {
    const currentItems = getValues('items') || [];
    const total = Number(getValues('sellingPrice') || 0);
    const input: RateioItemInput[] = currentItems.map((it) => ({
      itemPrice: it.itemPrice ?? null,
      sellingPrice: it.priceDetails?.sellingPrice ?? null,
    }));
    const resultado = rateioSellingPrice(total, input);
    resultado.forEach((res, index) => {
      if (res.rateado) {
        setValue(`items.${index}.priceDetails.sellingPrice`, res.sellingPrice ?? undefined, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    });
  }, [getValues, setValue]);

  const handleDistribuirClick = useCallback(() => {
    const currentItems = getValues('items') || [];
    const temPraticadoManual = currentItems.some(
      (it) => it.isSaved && it.priceDetails?.sellingPrice != null,
    );
    if (temPraticadoManual) {
      setShowRateioConfirm(true);
      return;
    }
    aplicarRateio();
  }, [getValues, aplicarRateio]);

  const goToItem = (index: number) => {
    window.dispatchEvent(new CustomEvent(EXPAND_ITEM_EVENT, { detail: { index } }));
    setTimeout(() => {
      document.getElementById(orderItemCardId(index))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  return (
    <>
      <Title>Revisão da Venda</Title>

      <FormGrid columns={3}>
        <FormGroup>
          <Label htmlFor="clientOrderNumber">Nº do Pedido do Cliente</Label>
          <Input id="clientOrderNumber" type="number" {...register('clientOrderNumber')} hasError={!!get(errors, 'clientOrderNumber')} placeholder="Digite o número" />
        </FormGroup>
      </FormGrid>

      <FechamentoContainer>
        <FechamentoHeader>
          <Summary>
            <SummaryItem>
              <StrongLabel>Itens do pedido</StrongLabel>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Itens:</SummaryLabel>
              <SummaryValue>{itensSalvos.length}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Peças:</SummaryLabel>
              <SummaryValue>{totalPecas > 0 ? totalPecas : '—'}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Total Cadastrado:</SummaryLabel>
              <SummaryValue>{totalPadrao > 0 ? formatCurrency(totalPadrao) : '—'}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Total de Venda:</SummaryLabel>
              <SummaryValue>{somaEfetivoItens > 0 ? formatCurrency(somaEfetivoItens) : '—'}</SummaryValue>
            </SummaryItem>
          </Summary>

          <HeaderPriceBlock>
            <SummaryLabel as="label" htmlFor="sellingPrice">Preço de Venda do Pedido</SummaryLabel>
            <HeaderPriceRow>
              <Controller
                name="sellingPrice"
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    id="sellingPrice"
                    name="sellingPrice"
                    value={field.value || 0}
                    onValueChange={(value, name, values) => {
                      field.onChange(values?.float || undefined);
                    }}
                    placeholder="R$ 0,00"
                    hasError={!!get(errors, 'sellingPrice')}
                  />
                )}
              />
              {mostrarDistribuir && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDistribuirClick}
                >
                  Distribuir pelos itens
                </Button>
              )}
            </HeaderPriceRow>
          </HeaderPriceBlock>
        </FechamentoHeader>

        {itensSalvos.length === 0 ? (
          <EmptyState>Nenhum item salvo ainda — salve os itens acima para fechar a venda.</EmptyState>
        ) : (
          <TableScroll>
            <ColHead>
              <span>Item</span>
              <span>Peças</span>
              <span>Preço Cadastrado</span>
              <span>Preço de Venda</span>
              <span>Desconto/Ajuste</span>
              <span>Subtotal</span>
              <span />
            </ColHead>

            <RowsList>
              {items.map((it: any, index: number) => {
                if (!it.isSaved) return null;
                const calculado = it.itemPrice != null ? Number(it.itemPrice) : 0;
                const venda = it.priceDetails?.sellingPrice != null ? Number(it.priceDetails.sellingPrice) : 0;
                const efetivo = venda > 0 ? venda : calculado;
                const desconto = calculado > 0 && venda > 0 ? ((calculado - venda) / calculado) * 100 : null;
                const isOpen = openIndex === index;
                const breakdown = isOpen ? buildBreakdown(it) : [];
                return (
                  <ItemRowCard key={index}>
                    <ItemRow>
                      <ItemName title={it.nomeCustomizado || ''}>
                        <strong>{index + 1} · {TYPE_LABELS[String(it.itemType)] || String(it.itemType)}</strong>
                        {it.nomeCustomizado ? ` — ${it.nomeCustomizado}` : ''}
                      </ItemName>
                      <Num muted={!(Number(it.quantityPieces) > 0)}>
                        {Number(it.quantityPieces) > 0 ? Number(it.quantityPieces) : '—'}
                      </Num>
                      <Num>{calculado > 0 ? formatCurrency(calculado) : '—'}</Num>
                      <Num strong={venda > 0} muted={!(venda > 0)}>
                        {venda > 0 ? formatCurrency(venda) : '—'}
                      </Num>
                      <Num
                        muted={desconto == null}
                        title={desconto == null ? undefined : desconto >= 0 ? 'Desconto sobre o cadastrado' : 'Ajuste sobre o cadastrado'}
                      >
                        {desconto == null ? '—' : desconto >= 0
                          ? `${desconto.toFixed(1).replace('.', ',')}%`
                          : `+${Math.abs(desconto).toFixed(1).replace('.', ',')}%`}
                      </Num>
                      <Num strong>{efetivo > 0 ? formatCurrency(efetivo) : '—'}</Num>
                      <ExpandButton
                        type="button"
                        isOpen={isOpen}
                        title={isOpen ? 'Recolher composição' : 'Ver composição do preço'}
                        onClick={() => setOpenIndex(isOpen ? null : index)}
                      >
                        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </ExpandButton>
                    </ItemRow>

                    {isOpen && (
                      <Breakdown>
                        {breakdown.length === 0 ? (
                          <BreakdownRow>
                            <BreakdownDesc>Composição sem valores detalhados.</BreakdownDesc>
                          </BreakdownRow>
                        ) : breakdown.map((line, i) => (
                          <BreakdownRow key={i}>
                            <BreakdownIndex>{index + 1}.{i + 1}</BreakdownIndex>
                            <TypeChip>{line.chip}</TypeChip>
                            <BreakdownDesc title={line.desc}>{line.desc}</BreakdownDesc>
                            {line.formula && <BreakdownFormula>{line.formula}</BreakdownFormula>}
                            <BreakdownValue>{line.value != null && line.value > 0 ? formatCurrency(line.value) : '—'}</BreakdownValue>
                          </BreakdownRow>
                        ))}
                        <AdjustLink type="button" onClick={() => goToItem(index)}>
                          <ArrowUp size={13} /> Ajustar preço do item
                        </AdjustLink>
                      </Breakdown>
                    )}
                  </ItemRowCard>
                );
              })}
            </RowsList>
          </TableScroll>
        )}

      </FechamentoContainer>

      <ConfirmationModal
        isOpen={showRateioConfirm}
        title="Itens com Preço de Venda manual"
        message="Um ou mais itens já têm Preço de Venda definido manualmente — esses valores serão preservados e o restante do total será distribuído entre os demais itens. Deseja continuar?"
        actions={[
          { label: 'Cancelar', onClick: () => setShowRateioConfirm(false), variant: 'outline' },
          {
            label: 'Distribuir',
            onClick: () => {
              aplicarRateio();
              setShowRateioConfirm(false);
            },
            variant: 'primary',
          },
        ]}
      />
    </>
  );
};

export default ReviewSection;
